import angular from 'angular';

export enum EditorHistoryTool {
  Undo = 'undo',
  Redo = 'redo'
}

export interface IEditorToolbarEditor {
  options: {
    tools?: Array<string | IEditorToolbarTool>;
  };
  element?: angular.IAugmentedJQuery;
  setOptions(options: { tools: Array<string | IEditorToolbarTool> }): void;
  refresh(): void;
}

export interface IEditorToolbarTool {
  name: string;
  tooltip?: string;
  palette?: string[] | null;
  template?: string;
  exec?: (event?: Event) => void;
}

export class EditorToolbarService {
  public static $inject: string[] = ['$compile'];

  private static readonly WRITING_ASSIST_TOOL_NAME = 'writingAssistButton';
  private static readonly HISTORY_LABELS = {
    [EditorHistoryTool.Undo]: 'Undo',
    [EditorHistoryTool.Redo]: 'Redo'
  } as const;
  private static readonly WRITING_ASSIST_LABEL = 'Improve writing';
  private static readonly WRITING_ASSIST_TITLE = 'Improve writing with AI';

  constructor(private readonly $compile?: angular.ICompileService) {}

  public createWritingAssistToolForEditor(
    actionsClass: string
  ): IEditorToolbarTool {
    return {
      name: EditorToolbarService.WRITING_ASSIST_TOOL_NAME,
      template: `<div class="editor-writing-assist-actions ${actionsClass}">${this.createWritingAssistButtonTemplate()}</div>`
    };
  }

  public createWritingAssistButtonTemplate(): string {
    return `<button type="button" class="text-button primary improve-writing-button" fg-writing-assist-button title="${EditorToolbarService.WRITING_ASSIST_TITLE}" aria-label="${EditorToolbarService.WRITING_ASSIST_TITLE}" data-testid="improve-writing-button"><span class="fa-light fa-wand-magic-sparkles" aria-hidden="true"></span><span class="editor-writing-assist-label">${EditorToolbarService.WRITING_ASSIST_LABEL}</span></button>`;
  }

  public createWritingAssistTool(
    editor: IEditorToolbarEditor,
    actionsClass: string,
    scope?: angular.IScope
  ): void {
    this.registerToolbarTool(
      editor,
      this.createWritingAssistToolForEditor(actionsClass),
      scope
    );
  }

  public createToolbarComponents(
    editor: IEditorToolbarEditor,
    scope: angular.IScope
  ): void {
    this.compileToolbarComponents(editor, scope);
  }

  public createHistoryToolForEditor(
    historyTool: EditorHistoryTool,
    testIdPrefix: string
  ): IEditorToolbarTool {
    const label = EditorToolbarService.HISTORY_LABELS[historyTool];
    const shortcut = historyTool === EditorHistoryTool.Undo ? 'Z' : 'Y';
    const testId = `${testIdPrefix}-${historyTool}-button`;

    return {
      name: historyTool,
      tooltip: `${label} (Ctrl+${shortcut})`,
      template: `<a href="javascript:void(0)" tabindex="0" role="button" class="k-tool" unselectable="on" title="${label} (Ctrl+${shortcut})" aria-label="${label}" data-testid="${testId}"><span unselectable="on" class="k-tool-icon k-icon k-i-${historyTool}"></span></a>`
    };
  }

  public createHistoryTool(
    editor: IEditorToolbarEditor,
    historyTool: EditorHistoryTool,
    testIdPrefix: string,
    scope?: angular.IScope
  ): void {
    this.registerToolbarTool(
      editor,
      this.createHistoryToolForEditor(historyTool, testIdPrefix),
      scope
    );
  }

  private registerToolbarTool(
    editor: IEditorToolbarEditor,
    toolbarTool: IEditorToolbarTool,
    scope?: angular.IScope
  ): void {
    const existingTools = editor.options.tools || [];
    const toolbarToolIndex = existingTools.findIndex(
      (tool: string | IEditorToolbarTool): boolean =>
        typeof tool === 'string'
          ? tool === toolbarTool.name
          : tool.name === toolbarTool.name
    );

    if (toolbarToolIndex >= 0) {
      if (typeof existingTools[toolbarToolIndex] === 'string') {
        const tools = [...existingTools];
        tools[toolbarToolIndex] = toolbarTool;
        editor.setOptions({ tools });
        editor.refresh();
      }

      this.compileToolbarComponents(editor, scope);
      return;
    }

    editor.setOptions({
      tools: [...existingTools, toolbarTool]
    });
    editor.refresh();
    this.compileToolbarComponents(editor, scope);
  }

  private compileToolbarComponents(
    editor: IEditorToolbarEditor,
    scope?: angular.IScope
  ): void {
    if (!this.$compile || !scope || !editor.element) {
      return;
    }

    const editorElement = (
      angular.element(editor.element) as unknown as {
        closest(selector: string): angular.IAugmentedJQuery;
      }
    ).closest('.k-editor');
    if (!editorElement.length) {
      return;
    }

    const toolbarComponents = editorElement[0].querySelectorAll<HTMLElement>(
      '[fg-writing-assist-button]'
    );

    toolbarComponents.forEach((element: HTMLElement): void => {
      const toolbarComponent = angular.element(element);
      if (toolbarComponent.data('fg-toolbar-compiled')) {
        return;
      }

      toolbarComponent.data('fg-toolbar-compiled', true);
      this.$compile(toolbarComponent)(scope);
    });
  }
}

angular.module('fg').service('editorToolbarService', EditorToolbarService);

export type EditorToolbarServiceType = InstanceType<
  typeof EditorToolbarService
>;
